import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { DifficultyId, GameMode, InputAction, INTERNAL_HEIGHT, INTERNAL_WIDTH } from '../game/constants';
import { R2_HEIGHT, R2_WIDTH } from '../game/render/r2/R2Renderer';
import { isR2RenderEnabled } from '../game/render/r2/flag';
import { GameRuntime, DEFAULT_SETTINGS, type GameSettings } from '../game/runtime/GameRuntime';
import type { RuntimeUISnapshot } from '../game/runtime/RuntimeUIStore';
import { GatePanel } from './components/GatePanel';
import { GateShop } from './components/GateShop';
import { Hud } from './components/Hud';
import { SettingsPanel } from './components/SettingsPanel';
import { TouchControls } from './components/TouchControls';
import {
  BootPanel,
  ControlsPanel,
  EndingPanel,
  ErrorPanel,
  GameOverPanel,
  PausePanel,
  StageResultPanel,
} from './components/panels';
import { TitleIntro, prefersReducedMotion, useTitleIntro } from './components/TitleIntro';
import { TitlePanel } from './components/TitlePanel';
import { useGamepadMenu } from './hooks/useGamepadMenu';
import './styles.css';

/** Sheet 07/08 departure: title scatter + camera push-in, then the stage cut. */
const DEPART_MS = 460;

const MODE_CLASS: Record<number, string> = {
  [GameMode.Boot]: 'boot',
  [GameMode.Title]: 'title',
  [GameMode.Controls]: 'controls',
  [GameMode.Playing]: 'playing',
  [GameMode.CalibrationGate]: 'gate',
  [GameMode.Paused]: 'paused',
  [GameMode.StageResult]: 'result',
  [GameMode.GameOver]: 'game-over',
  [GameMode.Ending]: 'ending',
  [GameMode.Error]: 'error',
};

function useCommitProbe(runtime: GameRuntime): void {
  useLayoutEffect(() => {
    runtime.performanceSampler?.noteReactCommit(performance.now());
  });
}

const TopRail = memo(function TopRail({ runtime }: { runtime: GameRuntime }) {
  const status = useSyncExternalStore(
    runtime.ui.subscribe,
    runtime.ui.getStatusSnapshot,
    runtime.ui.getServerStatusSnapshot,
  );
  useCommitProbe(runtime);
  return (
    <header className="top-rail" aria-label="Application status">
      <div className="brand-mark"><span className="brand-prism" aria-hidden="true" /> LUMINA ARCHIVE</div>
      <div className="runtime-status">
        <span>{status.renderer}</span>
        <span>AUDIO {status.audioState.toUpperCase()}</span>
        <span>{status.fps > 0 ? `${status.fps} FPS` : '60HZ LOGIC'}</span>
      </div>
    </header>
  );
});

const BottomRail = memo(function BottomRail({ runtime }: { runtime: GameRuntime }) {
  const status = useSyncExternalStore(
    runtime.ui.subscribe,
    runtime.ui.getStatusSnapshot,
    runtime.ui.getServerStatusSnapshot,
  );
  useCommitProbe(runtime);
  return (
    <footer className="bottom-rail">
      <span>INTERNAL {INTERNAL_WIDTH}×{INTERNAL_HEIGHT}</span>
      <span>INPUT {status.lastInputKind.toUpperCase()}</span>
      <span>DROPPED {status.droppedFrames}</span>
    </footer>
  );
});

const LiveHud = memo(function LiveHud({
  runtime,
  onPause,
}: {
  runtime: GameRuntime;
  onPause: () => void;
}) {
  const snapshot = useSyncExternalStore(
    runtime.ui.subscribe,
    runtime.ui.getSnapshot,
    runtime.ui.getServerSnapshot,
  );
  useCommitProbe(runtime);
  return <Hud snapshot={snapshot} onPause={onPause} />;
});

interface RuntimePanelsProps {
  readonly runtime: GameRuntime;
  readonly mode: GameMode;
  readonly r2Enabled: boolean;
  readonly onOpenSettings: () => void;
}

const RuntimePanels = memo(function RuntimePanels({
  runtime,
  mode,
  r2Enabled,
  onOpenSettings,
}: RuntimePanelsProps) {
  const snapshot: RuntimeUISnapshot = useSyncExternalStore(
    runtime.ui.subscribe,
    runtime.ui.getSnapshot,
    runtime.ui.getServerSnapshot,
  );
  useCommitProbe(runtime);
  if (mode === GameMode.CalibrationGate) {
    return r2Enabled
      ? <GateShop runtime={runtime} snapshot={snapshot} />
      : <GatePanel runtime={runtime} snapshot={snapshot} />;
  }
  if (mode === GameMode.Paused) {
    return <PausePanel runtime={runtime} snapshot={snapshot} onOpenSettings={onOpenSettings} />;
  }
  if (mode === GameMode.StageResult) return <StageResultPanel runtime={runtime} snapshot={snapshot} />;
  if (mode === GameMode.GameOver) return <GameOverPanel runtime={runtime} snapshot={snapshot} />;
  if (mode === GameMode.Ending) return <EndingPanel runtime={runtime} snapshot={snapshot} />;
  if (mode === GameMode.Error) return <ErrorPanel snapshot={snapshot} />;
  return null;
});

export default function App() {
  const runtime = useMemo(() => new GameRuntime(), []);
  // The simulation is 384x216 either way; only the backing store differs, and
  // both are 16:9, so the surface fit below is the same calculation on a
  // different base. GameRuntime reads the same flag to pick its renderer.
  const r2Enabled = useMemo(isR2RenderEnabled, []);
  const canvasWidth = r2Enabled ? R2_WIDTH : INTERNAL_WIDTH;
  const canvasHeight = r2Enabled ? R2_HEIGHT : INTERNAL_HEIGHT;
  const mode = useSyncExternalStore(
    runtime.ui.subscribe,
    runtime.ui.getModeSnapshot,
    runtime.ui.getServerModeSnapshot,
  );
  const viewportRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<HTMLElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioUnlockInFlightRef = useRef(false);
  const audioReadyRef = useRef(false);
  const [difficulty, setDifficulty] = useState<DifficultyId>(DifficultyId.Bright);
  const [settings, setSettings] = useState<GameSettings>({ ...DEFAULT_SETTINGS });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [departing, setDeparting] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [fullscreenActive, setFullscreenActive] = useState(false);
  const [fullscreenHelpOpen, setFullscreenHelpOpen] = useState(false);
  const departTimerRef = useRef<number | null>(null);
  const introPhase = useTitleIntro(mode);
  // maxTouchPoints also catches iPhone Safari's "desktop website" mode, where
  // the pointer media query can report a fine pointer despite touch being the
  // only practical input.
  const isTouchDevice = useMemo(
    () => navigator.maxTouchPoints > 0 || window.matchMedia('(pointer: coarse)').matches,
    [],
  );

  useLayoutEffect(() => {
    runtime.performanceSampler?.noteReactCommit(performance.now());
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    void runtime.init(canvas);
    return () => runtime.dispose();
  }, [runtime]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const surface = surfaceRef.current;
    if (!viewport || !surface) return;
    const resize = (): void => {
      const rect = viewport.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return;
      // Portrait gameplay must preserve the whole 16:9 field. Portrait menus,
      // however, are more legible when the cinematic background covers the
      // viewport and the centred DOM panel stays at a useful physical size.
      const portraitMenu = isTouchDevice
        && rect.height > rect.width
        && (mode !== GameMode.Playing || settingsOpen);
      const rawScale = portraitMenu
        ? Math.max(rect.width / canvasWidth, rect.height / canvasHeight)
        : Math.min(rect.width / canvasWidth, rect.height / canvasHeight);
      // Desktop still snaps to an integer multiple above 1x to keep pixel art
      // crisp on a mouse-precision display. Coarse-pointer devices fill the
      // viewport maximally instead — a phone never gets the luxury of an exact
      // integer scale, and a small crisp island surrounded by dead space is
      // worse than a very slightly soft full-bleed surface.
      const scale = isTouchDevice ? rawScale : (rawScale >= 1 ? Math.max(1, Math.floor(rawScale)) : rawScale);
      surface.style.width = `${Math.max(1, Math.floor(canvasWidth * scale))}px`;
      surface.style.height = `${Math.max(1, Math.floor(canvasHeight * scale))}px`;
    };
    const observer = new ResizeObserver(resize);
    observer.observe(viewport);
    resize();
    return () => observer.disconnect();
  }, [canvasHeight, canvasWidth, isTouchDevice, mode, settingsOpen]);

  const unlockAudio = useCallback((): void => {
    if (
      (audioReadyRef.current && !runtime.audio.needsUserGesture)
      || audioUnlockInFlightRef.current
    ) return;
    audioUnlockInFlightRef.current = true;
    void runtime.unlockAudio()
      .then((ready) => {
        audioReadyRef.current = ready;
        setAudioReady(ready);
      })
      .finally(() => {
        // iOS may reject the first resume attempt while the page is settling.
        // Releasing the guard lets the next real tap retry instead of leaving
        // audio permanently suspended for the rest of the session.
        audioUnlockInFlightRef.current = false;
      });
  }, [runtime]);

  useEffect(() => {
    const requireAudioRecheck = (): void => {
      if (document.visibilityState !== 'visible') return;
      audioReadyRef.current = false;
      setAudioReady(false);
    };
    document.addEventListener('visibilitychange', requireAudioRecheck);
    window.addEventListener('pageshow', requireAudioRecheck);
    return () => {
      document.removeEventListener('visibilitychange', requireAudioRecheck);
      window.removeEventListener('pageshow', requireAudioRecheck);
    };
  }, []);

  useEffect(() => {
    const sync = (): void => {
      const legacyDocument = document as Document & { webkitFullscreenElement?: Element | null };
      setFullscreenActive(Boolean(document.fullscreenElement ?? legacyDocument.webkitFullscreenElement));
    };
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    sync();
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, []);

  const enterFullscreen = useCallback((): void => {
    const target = appRef.current as (HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    }) | null;
    if (!target) return;
    const request = target.requestFullscreen
      ? () => target.requestFullscreen({ navigationUI: 'hide' })
      : target.webkitRequestFullscreen
        ? () => target.webkitRequestFullscreen?.()
        : null;
    if (!request) {
      setFullscreenHelpOpen(true);
      return;
    }
    void Promise.resolve(request()).catch(() => setFullscreenHelpOpen(true));
  }, []);

  const updateSettings = useCallback((next: GameSettings): void => {
    setSettings(next);
    runtime.applySettings(next);
  }, [runtime]);

  const closeOrBack = useCallback((): void => {
    if (settingsOpen) {
      setSettingsOpen(false);
      return;
    }
    if (mode === GameMode.Controls) runtime.returnToTitle();
    else if (mode === GameMode.Paused) runtime.resume();
    else if (mode === GameMode.CalibrationGate) runtime.leaveGate();
  }, [mode, runtime, settingsOpen]);

  const menuActive = settingsOpen || mode !== GameMode.Playing;
  const blocksHeldFire = mode === GameMode.CalibrationGate || mode === GameMode.StageResult;
  useGamepadMenu({ active: menuActive, root: appRef, onCancel: closeOrBack });

  useEffect(() => {
    if (!menuActive) return;
    const raf = requestAnimationFrame(() => {
      const host = appRef.current;
      const focused = document.activeElement;
      if (focused instanceof HTMLElement && host?.contains(focused)) return;
      // preventScroll: focus-driven scroll can shove the overflow-hidden game
      // surface off-screen on phones (see the .game-canvas !important note).
      host?.querySelector<HTMLElement>('[data-menu-item]:not([disabled])')?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(raf);
  }, [menuActive, mode, settingsOpen]);

  useEffect(() => {
    if (!menuActive) return;
    const onMenuKeyDown = (event: KeyboardEvent): void => {
      if (event.code !== 'Escape') return;
      // InputManager owns Escape while focus is on the game surface; menus
      // autofocus a native button, which InputManager deliberately ignores —
      // handling only that case keeps the two paths from double-toggling.
      const target = event.target;
      const onUiControl = target instanceof HTMLElement
        && target.closest('button, input, select, textarea, a, [role="button"]') !== null;
      if (!onUiControl) return;
      event.preventDefault();
      closeOrBack();
    };
    window.addEventListener('keydown', onMenuKeyDown);
    return () => window.removeEventListener('keydown', onMenuKeyDown);
  }, [menuActive, closeOrBack]);

  useEffect(() => {
    if (!blocksHeldFire) return;
    // A Space press that began as gameplay fire must not become native-button
    // activation after the shop/result overlay takes focus.
    runtime.input.releaseKeyboardAction(InputAction.Fire);
    const blockSpace = (event: KeyboardEvent): void => {
      if (event.code !== 'Space') return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', blockSpace, { capture: true, passive: false });
    window.addEventListener('keyup', blockSpace, { capture: true, passive: false });
    return () => {
      window.removeEventListener('keydown', blockSpace, { capture: true });
      window.removeEventListener('keyup', blockSpace, { capture: true });
    };
  }, [blocksHeldFire, runtime]);

  const openSettings = useCallback((): void => setSettingsOpen(true), []);
  const closeSettings = useCallback((): void => setSettingsOpen(false), []);
  // Fix 3(b): stable identity so Hud's React.memo can actually skip re-rendering
  // (an inline arrow here would give Hud a new onPause prop every App render).
  const pauseGame = useCallback((): void => runtime.pause(), [runtime]);

  // Ref guard rather than `disabled`: disabling the button would repaint it
  // mid-dissolve, which is exactly the frame the transition is trying to sell.
  const beginRestoration = useCallback((): void => {
    if (departTimerRef.current !== null) return;
    if (prefersReducedMotion()) {
      runtime.startGame(difficulty);
      return;
    }
    setDeparting(true);
    departTimerRef.current = window.setTimeout(() => {
      departTimerRef.current = null;
      setDeparting(false);
      runtime.startGame(difficulty);
    }, DEPART_MS);
  }, [difficulty, runtime]);

  useEffect(() => () => {
    if (departTimerRef.current !== null) window.clearTimeout(departTimerRef.current);
  }, []);

  return (
    <main
      ref={appRef}
      className={`app-shell mode-${MODE_CLASS[mode] ?? 'unknown'}${departing ? ' departing' : ''}${isTouchDevice ? ' touch-device' : ''}`}
      onPointerDownCapture={unlockAudio}
      onTouchEndCapture={unlockAudio}
      onClickCapture={unlockAudio}
      onKeyDownCapture={unlockAudio}
    >
      <TopRail runtime={runtime} />

      <div ref={viewportRef} className="game-viewport">
        <div ref={surfaceRef} className="game-surface">
          <canvas
            ref={canvasRef}
            className="game-canvas"
            width={canvasWidth}
            height={canvasHeight}
            aria-label="LUMINA ARCHIVE game display"
          />
          <div className="scanline-mask" aria-hidden="true" />
          <div className="ui-layer" data-intro={introPhase}>
            {(mode === GameMode.Playing || mode === GameMode.CalibrationGate || mode === GameMode.Paused) && (
              <LiveHud runtime={runtime} onPause={pauseGame} />
            )}

            {mode === GameMode.Boot && <BootPanel />}

            {mode === GameMode.Title && !settingsOpen && (
              <TitlePanel
                runtime={runtime}
                difficulty={difficulty}
                departing={departing}
                onSelectDifficulty={setDifficulty}
                onBegin={beginRestoration}
                onOpenSettings={openSettings}
              />
            )}

            {mode === GameMode.Controls && !settingsOpen && <ControlsPanel runtime={runtime} />}

            {!settingsOpen && (mode === GameMode.CalibrationGate
              || mode === GameMode.Paused
              || mode === GameMode.StageResult
              || mode === GameMode.GameOver
              || mode === GameMode.Ending
              || mode === GameMode.Error) && (
              <RuntimePanels
                runtime={runtime}
                mode={mode}
                r2Enabled={r2Enabled}
                onOpenSettings={openSettings}
              />
            )}

            {settingsOpen ? <SettingsPanel settings={settings} onChange={updateSettings} onClose={closeSettings} /> : null}

            <TitleIntro phase={introPhase} />
          </div>
        </div>

        <TouchControls
          input={runtime.input}
          world={runtime.world}
          opacity={settings.touchOpacity}
          visible={isTouchDevice && mode === GameMode.Playing && !settingsOpen}
        />

        {isTouchDevice && mode !== GameMode.Boot && !audioReady && (
          <button
            type="button"
            className="mobile-audio-unlock"
            onPointerDown={unlockAudio}
            onTouchEnd={unlockAudio}
            onClick={unlockAudio}
          >
            <strong>SOUND ON</strong>
            <small>TAP TO ENABLE</small>
          </button>
        )}

        {isTouchDevice && mode !== GameMode.Boot && !fullscreenActive && (
          <button type="button" className="mobile-fullscreen" onClick={enterFullscreen}>
            <strong>FULL SCREEN</strong>
            <small>EXPAND PLAY AREA</small>
          </button>
        )}

        {fullscreenHelpOpen && (
          <div className="fullscreen-help" role="dialog" aria-modal="true" aria-label="Fullscreen instructions">
            <div>
              <strong>FULL SCREEN</strong>
              <p>Safariの共有メニューから「ホーム画面に追加」し、ホーム画面のアイコンから起動してください。</p>
              <button type="button" className="button primary" onClick={() => setFullscreenHelpOpen(false)}>CLOSE</button>
            </div>
          </div>
        )}
      </div>

      <BottomRail runtime={runtime} />
    </main>
  );
}
